/**
 * Guarda de permiso `manage_talonarios` para las pantallas de Talonarios.
 *
 * La ruta ya cuelga de `RequireAuth`; el gating de permiso lo hace la pantalla
 * (como `OrderNewScreen` con `add_orders`). Replica la paridad con el SSR
 * (`docs/frontend_architecture.md`, Pr. 5): sin el permiso `manage_talonarios` se
 * muestra un aviso en vez del contenido. Reutilizable por las cinco pantallas del
 * dominio (supervisor, detalle, historial, descuadres, histórico).
 */
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { MANAGE_TALONARIOS_PERMISSION } from "../constants";

interface RequireManageTalonariosProps {
  /** Título a mostrar en el aviso de "sin permiso". */
  title: string;
  children: ReactNode;
}

export function RequireManageTalonarios({
  title,
  children,
}: RequireManageTalonariosProps) {
  const { status, hasPermission } = useAuth();

  // Mientras la sesión se hidrata (`/auth/me`), `hasPermission` aún no conoce los
  // permisos: mostrar el aviso "sin permiso" en ese instante sería engañoso. Se
  // espera a tener la sesión resuelta antes de decidir (la ruta ya está protegida
  // por `RequireAuth`, que redirige a login si no hay sesión).
  if (status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500" role="status">
            Cargando…
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!hasPermission(MANAGE_TALONARIOS_PERMISSION)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            No tienes permiso para gestionar talonarios.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
